import React from 'react';
import { Clock, ShoppingCart, CreditCard, CheckCircle, Truck } from 'lucide-react';
import { Helmet } from 'react-helmet';

const HowItWorks = () => {
  return (
    <>
      <Helmet>
        <meta name="description" content="Learn how our simple ordering process works. Sign up, browse our menu, make a payment via transfer, and track your order in real time." />
        <meta name="keywords" content="how it works, order process, payment, delivery, tracking, online ordering" />
        <meta property="og:title" content="How It Works - Simple Ordering Process" />
        <meta property="og:description" content="Our easy 5-step ordering process: sign up, select items, transfer payment, order processing, and real-time tracking." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/how-it-works" />
        <link rel="canonical" href="/how-it-works" />
      </Helmet>

      <div className="bg-black text-white py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-red-500 mb-6 text-center">How It Works</h2>
          <p className="text-lg text-center mb-10">Getting started is easy! Follow these simple steps:</p>
          
          <ol className="relative border-s border-gray-700">
            <li className="mb-10 ms-6">
              <span className="absolute flex items-center justify-center w-6 h-6 bg-red-900 rounded-full -start-3 ring-8 ring-black">
                <Clock className="w-3 h-3 text-red-300" />
              </span>
              <h3 className="flex items-center mb-1 text-lg font-semibold text-white">
                Sign up or create an account
                <span className="bg-red-900 text-red-300 text-sm font-medium me-2 px-2.5 py-0.5 rounded-sm ms-3">Step 1</span>
              </h3>
              <time className="block mb-2 text-sm font-normal leading-none text-gray-400">Get started in seconds</time>
              <p className="mb-4 text-base font-normal text-gray-400">Create your account to access all features and place orders.</p>
            </li>
            
            <li className="mb-10 ms-6">
              <span className="absolute flex items-center justify-center w-6 h-6 bg-red-900 rounded-full -start-3 ring-8 ring-black">
                <ShoppingCart className="w-3 h-3 text-red-300" />
              </span>
              <h3 className="mb-1 text-lg font-semibold text-white">Browse our menu and place your order</h3>
              <time className="block mb-2 text-sm font-normal leading-none text-gray-400">Select your favorites</time>
              <p className="text-base font-normal text-gray-400">Choose from our wide selection of items and customize your order to your liking.</p>
            </li>
            
            <li className="mb-10 ms-6">
              <span className="absolute flex items-center justify-center w-6 h-6 bg-red-900 rounded-full -start-3 ring-8 ring-black">
                <CreditCard className="w-3 h-3 text-red-300" />
              </span>
              <h3 className="mb-1 text-lg font-semibold text-white">Make a payment via transfer</h3>
              <time className="block mb-2 text-sm font-normal leading-none text-gray-400">Choose from two account options</time>
              <p className="text-base font-normal text-gray-400">We accept bank transfers only. Choose either of our two bank accounts to complete your payment.</p>
            </li>
            
            <li className="mb-10 ms-6">
              <span className="absolute flex items-center justify-center w-6 h-6 bg-red-900 rounded-full -start-3 ring-8 ring-black">
                <CheckCircle className="w-3 h-3 text-red-300" />
              </span>
              <h3 className="mb-1 text-lg font-semibold text-white">We process your order</h3>
              <time className="block mb-2 text-sm font-normal leading-none text-gray-400">Order confirmation</time>
              <p className="text-base font-normal text-gray-400">Our team will prepare your order with care after confirming your payment.</p>
            </li>
            
            <li className="ms-6">
              <span className="absolute flex items-center justify-center w-6 h-6 bg-red-900 rounded-full -start-3 ring-8 ring-black">
                <Truck className="w-3 h-3 text-red-300" />
              </span>
              <h3 className="mb-1 text-lg font-semibold text-white">Track your order in real time</h3>
              <time className="block mb-2 text-sm font-normal leading-none text-gray-400">Delivery or pickup</time>
              <p className="text-base font-normal text-gray-400">Check the orders tab menu to see your order status, which is updated live. For pickup, you'll see when it's ready. For delivery, you'll know when it's out for dispatch!</p>
            </li>
          </ol>
        </div>
      </div>
    </>
  );
};

export default HowItWorks;